import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import ToggleSwitchPanel from 'components/form/ToggleSwitchPanel.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { nowAsDate } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import generateIntegers from 'utils/numbers/generateIntegers.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';

import CN from './MonthYearPicker.module.scss';

const L = logger('MonthYearPicker'); // eslint-disable-line no-unused-vars

const TYPE = Object.freeze({
  cancel: 'cancel',
  init: 'init',
  apply: 'apply',
});

const MSG_TOO_MANY_YEARS = 'staffKpisReports.kpiReports.monthYearPicker.messageManyYears';

const MONTHS = Object.freeze(['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']);
const DISPLAY = Object.freeze({
  jan: 'Jan',
  feb: 'Feb',
  mar: 'Mar',
  apr: 'Apr',
  may: 'May',
  jun: 'Jun',
  jul: 'Jul',
  aug: 'Aug',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dec: 'Dec',
});

const fromMonthToFullString = (month) => DISPLAY[month] || 'staffKpisReports.kpiReports.monthYearPicker.Unknown';

const TextBoth = ({ months, year, translation: t }) =>
  !months?.length ? (
    <>
      {t('staffKpisReports.kpiReports.monthYearPicker.compare')}{' '}
      <div
        className={CN.hiddenOnMobile}
      >{`${t('staffKpisReports.kpiReports.monthYearPicker.months')}/${t('staffKpisReports.kpiReports.monthYearPicker.years')}`}</div>
    </>
  ) : (
    <div className={CN.selectedText}>
      <div className={CN.displaySelectedText}>{asArray(months).map(fromMonthToFullString).join(', ')}</div>
      <div>{year}</div>
    </div>
  );

const TextYears = ({ years, translation: t }) =>
  years.length === 0 ? (
    <>
      {t('staffKpisReports.kpiReports.monthYearPicker.compare')}{' '}
      <div
        className={CN.hiddenOnMobile}
      >{`${t('staffKpisReports.kpiReports.monthYearPicker.months')}/${t('staffKpisReports.kpiReports.monthYearPicker.years')}`}</div>
    </>
  ) : (
    <div className={CN.selectedText}>{years.join(', ')}</div>
  );

const THIS_YEAR = nowAsDate().getFullYear();
// NOTE: current year will seldom change, so just precalculate a list and keep it unchanging until page refresh
const DEFAULT_YEAR_OPTIONS = Object.freeze(generateIntegers({ bgn: THIS_YEAR - 9, end: THIS_YEAR + 2 }));

const MonthYearPicker = ({
  initCurrentYear,
  initAvailableYears,
  onApply,
  onCancel = alwaysNoop,
  onChange = alwaysNoop,
  onInit = alwaysNoop,
  unreleased,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isOpen, yay: open, nay: close } = useTip(false);
  const { val: isMonths, inv: isYears, yay: swapToMonths, nay: swapToYears } = useTip(true);
  const { val: isFull, inv: isUntilNow, yay: swapToFull, nay: swapToUntilNow } = useTip(true);

  const yearOptions = isNotFullArray(initAvailableYears) ? DEFAULT_YEAR_OPTIONS : initAvailableYears;
  const [displayedYear, setDisplayedYear] = useState(initCurrentYear ?? THIS_YEAR);

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);

  const [warningMessage, setWarningMessage] = useState('');
  const clearMessage = useCallback(() => void setWarningMessage(''), []);

  const handlePrevYear = useCallback(() => void setDisplayedYear(displayedYear - 1), [displayedYear]);
  const handleNextYear = useCallback(() => void setDisplayedYear(displayedYear + 1), [displayedYear]);

  const memoizedEvent = useMemo(() => {
    const result = {
      months: selectedMonths,
      years: selectedYears,
      yearOptions,
      displayedYear,
      isOpen,
      isMonths,
      isYears,
      isFull,
      isUntilNow,
    };

    L.debug('()', result);
    return result;
  }, [displayedYear, isFull, isMonths, isOpen, isUntilNow, isYears, selectedMonths, selectedYears, yearOptions]);

  const handleCancel = useCallback(() => {
    close();
    const event = { ...memoizedEvent, type: TYPE.cancel };
    call('MonthYearPicker.onCancel', onCancel, event);
    call('MonthYearPicker.onChange', onChange, event);
  }, [close, memoizedEvent, onCancel, onChange]);

  const handleOpen = useCallback(() => {
    open();
    const event = { ...memoizedEvent, type: TYPE.init };
    call('MonthYearPicker.onInit', onInit, event);
    call('MonthYearPicker.onChange', onChange, event);
  }, [open, memoizedEvent, onInit, onChange]);

  const handleApply = useCallback(() => {
    close();
    const event = { ...memoizedEvent, type: TYPE.apply };
    call('MonthYearPicker.onApply', onApply, event);
    call('MonthYearPicker.onChange', onChange, event);
  }, [close, memoizedEvent, onApply, onChange]);

  const handleSelectMonth = useCallback(
    (month) => {
      if (isMonths) {
        setSelectedMonths(
          selectedMonths.includes(month)
            ? selectedMonths.filter((selectedMonth) => selectedMonth !== month)
            : [...selectedMonths, month],
        );
      } else {
        setSelectedYears([]);
      }
    },
    [isMonths, selectedMonths],
  );

  const handleSelectYear = useCallback(
    (year) => {
      clearMessage();

      if (!isYears) setSelectedMonths([]);

      if (selectedYears.includes(year)) {
        return void setSelectedYears(selectedYears.filter((selectedYear) => selectedYear !== year));
      }

      if (selectedYears.length < 3) {
        return void setSelectedYears([...selectedYears, year]);
      }

      setWarningMessage(MSG_TOO_MANY_YEARS);
    },
    [clearMessage, isYears, selectedYears],
  );

  const modalRef = useRef(null);
  useClickAway(modalRef, handleCancel);

  const title = t('staffKpisReports.kpiReports.monthYearPicker.selectPeriodToCompare', {
    period: isMonths
      ? t('staffKpisReports.kpiReports.monthYearPicker.months')
      : t('staffKpisReports.kpiReports.monthYearPicker.years'),
  });
  const isApplyDisabled = (isMonths && isNotFullArray(selectedMonths)) || (isYears && isNotFullArray(selectedYears));
  const isButtonSelected = !isOpen && (selectedMonths.length > 0 || selectedYears.length > 0);

  const renderedButton = (
    <Button
      variant="quinary"
      active={isOpen}
      selected={isButtonSelected}
      tag="MonthYearPicker--button"
      height="medium"
      onClick={unreleased ? alwaysNoop : handleOpen}
    >
      <div className={CN.buttonInner}>
        <Icon variant="clipboardDoc" width="md" />
        <div
          className={cn({
            [CN.text]: true,
            [CN.bold]: isOpen,
          })}
        >
          {isMonths ? (
            <TextBoth months={selectedMonths} year={displayedYear} translation={t} />
          ) : (
            <TextYears years={selectedYears} translation={t} />
          )}
        </div>
      </div>
    </Button>
  );

  return (
    <div
      data-bem="MonthYearPicker"
      data-is-open={isOpen}
      data-is-years={isYears}
      data-is-months={isMonths}
      data-is-full={isFull}
      data-is-until={isUntilNow}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {unreleased ? (
        <Tooltip variant="slickTop" width="min" content={t(unreleased)}>
          {renderedButton}
        </Tooltip>
      ) : (
        renderedButton
      )}

      {isOpen && (
        <div data-bem="MonthYearPicker--panel" ref={modalRef}>
          <div className={CN.pickerPanel}>
            <div className={CN.pickerBody}>
              <div className={CN.title}>{t('staffKpisReports.kpiReports.monthYearPicker.title')}</div>
              <div className={CN.flex}>
                <ToggleSwitchPanel>
                  <ToggleSwitch selected={isMonths} onClick={swapToMonths} name="Month" size="normal" />
                  <ToggleSwitch selected={isYears} onClick={swapToYears} name="Year" size="normal" />
                </ToggleSwitchPanel>
                <ToggleSwitchPanel>
                  <ToggleSwitch selected={isFull} onClick={swapToFull} name="Full" size="normal" />
                  <ToggleSwitch selected={isUntilNow} onClick={swapToUntilNow} name="To-date" size="normal" />
                </ToggleSwitchPanel>
              </div>
              <div className={CN.title}>{title}</div>

              {isMonths ? (
                <div>
                  <div className={CN.navButtonsYear}>
                    <Button variant="bin" onClick={handlePrevYear}>
                      <Icon variant="arrowPrev" width="lg" />
                    </Button>
                    <span className={CN.currentYearText}>{displayedYear}</span>
                    <Button variant="bin" onClick={handleNextYear}>
                      <Icon variant="arrowNext" width="lg" />
                    </Button>
                  </div>

                  <div className={CN.grid}>
                    {MONTHS.map((month, index) => (
                      <div
                        className={cn(CN.pickerButton, {
                          [CN.selected]: selectedMonths.includes(month),
                        })}
                        key={index}
                        onClick={() => handleSelectMonth(month)}
                      >
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={CN.grid}>
                  {yearOptions.map((year) => (
                    <div
                      className={cn(CN.pickerButton, {
                        [CN.selected]: selectedYears.includes(year),
                      })}
                      key={year}
                      onClick={() => handleSelectYear(year)}
                    >
                      {year}
                    </div>
                  ))}
                </div>
              )}
              {warningMessage && !isMonths && <div className={CN.error}>{t(warningMessage)}</div>}
            </div>
            <div className={CN.pickerFooter}>
              <Button variant="outline" onClick={handleCancel}>
                {t('common.buttons.cancelUpp')}
              </Button>
              <Button variant="primaryForce" onClick={handleApply} disabled={isApplyDisabled}>
                {t('common.buttons.applyUpp')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MonthYearPicker.TYPE = TYPE;

export default MonthYearPicker;
